import { createRouter, createWebHistory } from "vue-router";
import {
  getAccessToken,
  clearAccessToken,
  fetchOnboardingStatus,
  isSessionInvalidError,
  restoreAccessSession,
} from "./services/api.js";
import { persistLastVisitedRoute } from "./services/lastVisitedRoute.js";

const onboardingStepRoutes = {
  1: "/onboarding/photo",
  2: "/onboarding/profile-basics",
  3: "/onboarding/privacy",
  4: "/onboarding/first-wishlist",
  5: "/onboarding/first-item",
  6: "/onboarding/invite-friends",
  7: "/onboarding/invite-friends/search",
  8: "/onboarding/complete",
};

const defaultPostOnboardingPath = "/feed";

const routes = [
  {
    path: "/startup",
    name: "startup",
    component: () => import("./views/StartupView.vue"),
    meta: { showHeader: false, publicPage: true, showBottomNav: false, startupPage: true },
  },
  {
    path: "/",
    name: "home",
    // dynamic imports for code splitting and lazy-loading
    component: () => import("./views/HomeView.vue"),
    meta: { showHeader: false, publicPage: true, showBottomNav: false }
  },
  {
    path: "/how-it-works",
    name: "how-it-works",
    component: () => import("./views/HowItWorksView.vue"),
    meta: { showHeader: false, publicPage: true, showBottomNav: false }
  },
  {
    path: "/features",
    name: "features",
    component: () => import("./views/FeaturesView.vue"),
    meta: { showHeader: false, publicPage: true, showBottomNav: false }
  },
  {
    path: "/privacy",
    name: "privacy",
    component: () => import("./views/PrivacyView.vue"),
    meta: { showHeader: false, publicPage: true, showBottomNav: false }
  },
  {
    path: "/terms",
    name: "terms",
    component: () => import("./views/TermsView.vue"),
    meta: { showHeader: false, publicPage: true, showBottomNav: false }
  },
  {
    path: "/support",
    name: "support",
    component: () => import("./views/SupportView.vue"),
    meta: { showHeader: false, publicPage: true, showBottomNav: false }
  },
  {
    path: "/about",
    name: "about",
    component: () => import("./views/AboutView.vue"),
    meta: { showHeader: false, publicPage: true, showBottomNav: false }
  },
  {
    path: "/auth/login",
    name: "login",
    component: () => import("./views/LoginView.vue"),
    meta: { showHeader: false, authPage: true, showBottomNav: false },
  },
  {
    path: "/auth/register",
    name: "register",
    component: () => import("./views/RegisterView.vue"),
    meta: { showHeader: false, authPage: true, showBottomNav: false },
  },
  {
    path: "/auth/verify-email",
    name: "verify-email-pending",
    component: () => import("./views/VerifyEmailPending.vue"),
    meta: { showHeader: false, authPage: true, showBottomNav: false },
  },
  {
    path: "/auth/verify-email/:token",
    name: "verify-email-token",
    component: () => import("./views/VerifyEmailSuccess.vue"),
    meta: { showHeader: false, authPage: true, showBottomNav: false },
  },
  {
    path: "/auth/reset-password",
    name: "reset-password-request",
    component: () => import("./views/ResetPasswordRequest.vue"),
    meta: { showHeader: false, authPage: true, showBottomNav: false },
  },
  {
    path: "/auth/reset-password/sent",
    name: "reset-password-sent",
    component: () => import("./views/ResetPasswordSent.vue"),
    meta: { showHeader: false, authPage: true, showBottomNav: false },
  },
  {
    path: "/auth/reset-password/:token",
    name: "reset-password-new",
    component: () => import("./views/ResetPasswordNew.vue"),
    meta: { showHeader: false, authPage: true, showBottomNav: false },
  },
  {
    path: "/onboarding/photo",
    name: "onboarding-photo",
    component: () => import("./views/OnboardingPhoto.vue"),
    meta: { showHeader: false, onboardingStep: true, showBottomNav: false },
  },
  {
    path: "/onboarding/profile-basics",
    name: "onboarding-profile-basics",
    component: () => import("./views/OnboardingProfileBasics.vue"),
    meta: { showHeader: false, onboardingStep: true, showBottomNav: false },
  },
  {
    path: "/onboarding/privacy",
    name: "onboarding-privacy",
    component: () => import("./views/OnboardingPrivacy.vue"),
    meta: { showHeader: false, onboardingStep: true, showBottomNav: false },
  },
  {
    path: "/onboarding/first-wishlist",
    name: "onboarding-first-wishlist",
    component: () => import("./views/OnboardingFirstWishlist.vue"),
    meta: { showHeader: false, onboardingStep: true, showBottomNav: false },
  },
  {
    path: "/onboarding/first-item",
    name: "onboarding-first-item",
    component: () => import("./views/OnboardingFirstItem.vue"),
    meta: { showHeader: false, onboardingStep: true, showBottomNav: false },
  },
  {
    path: "/onboarding/invite-friends",
    name: "onboarding-invite-friends",
    component: () => import("./views/OnboardingInviteFriends.vue"),
    meta: { showHeader: false, onboardingStep: true, showBottomNav: false },
  },
  {
    path: "/onboarding/invite-friends/search",
    name: "onboarding-invite-search",
    component: () => import("./views/OnboardingInviteSearch.vue"),
    meta: { showHeader: false, onboardingStep: true, showBottomNav: false },
  },
  {
    path: "/onboarding/complete",
    name: "onboarding-complete",
    component: () => import("./views/OnboardingComplete.vue"),
    meta: { showHeader: false, onboardingStep: true, showBottomNav: false },
  }
  ,
  {
    path: "/feed",
    name: "feed",
    component: () => import("./views/FeedView.vue"),
    meta: { showHeader: true, mainBottomPadding: false },
  },
  {
    path: "/search",
    name: "search",
    component: () => import("./views/SearchView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/notifications",
    name: "notifications",
    component: () => import("./views/NotificationsView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/notifications/preferences",
    name: "notification-preferences",
    component: () => import("./views/NotificationPreferencesView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/friends",
    name: "friends",
    component: () => import("./views/FriendsView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/friends/requests/received",
    name: "friend-requests-received",
    component: () => import("./views/FriendRequestsReceivedView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/friends/requests/sent",
    name: "friend-requests-sent",
    component: () => import("./views/FriendRequestsSentView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/friends/blocked",
    name: "blocked-users",
    component: () => import("./views/BlockedUsersView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/u/:username",
    name: "public-profile",
    component: () => import("./views/PublicProfileView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/u/:username/stories",
    name: "user-stories",
    component: () => import("./views/UserStoriesView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/u/:username/stories/:storyId",
    name: "user-story-detail",
    component: () => import("./views/UserStoryDetailView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/u/:username/events",
    name: "user-events",
    component: () => import("./views/UserEventsView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/u/:username/events/:eventId",
    name: "user-event-detail",
    component: () => import("./views/UserEventDetailView.vue"),
    meta: { showHeader: true, publicPage: true },
  },
  {
    path: "/me/friends/add",
    name: "add-friend",
    component: () => import("./views/AddFriendView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/friends/add/search",
    name: "add-friend-search",
    component: () => import("./views/AddFriendSearchView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/events/create",
    name: "event-create",
    component: () => import("./views/EventCreateView.vue"),
    meta: { showHeader: true, isForm: true },
  },
  {
    path: "/me/events",
    name: "events-list",
    component: () => import("./views/EventsListView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/events/:id",
    name: "event-detail",
    component: () => import("./views/EventDetailView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/events/:id/edit",
    name: "event-edit",
    component: () => import("./views/EventEditView.vue"),
    meta: { showHeader: true, isForm: true },
  },
  {
    path: "/me/profile",
    name: "profile",
    component: () => import("./views/ProfileOverviewView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/edit",
    name: "profile-edit",
    component: () => import("./views/ProfileEditView.vue"),
    meta: { showHeader: true, isForm: true },
  },
  {
    path: "/me/sizes",
    name: "sizes-list",
    component: () => import("./views/SizesListView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/sizes/new",
    name: "size-create",
    component: () => import("./views/SizeEditView.vue"),
    meta: { showHeader: true, isForm: true },
  },
  {
    path: "/me/sizes/:sizeId",
    name: "size-detail",
    component: () => import("./views/SizeDetailView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/sizes/:sizeId/edit",
    name: "size-edit",
    component: () => import("./views/SizeEditView.vue"),
    meta: { showHeader: true, isForm: true },
  },
  {
    path: "/me/support/bug-report",
    name: "bug-report",
    component: () => import("./views/BugReportView.vue"),
    meta: { showHeader: true, isForm: true },
  },
  {
    path: "/me/stories",
    name: "my-stories",
    component: () => import("./views/MyStoriesView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/stories/create",
    name: "story-create",
    component: () => import("./views/StoryEditView.vue"),
    meta: { showHeader: true, isForm: true },
  },
  {
    path: "/me/stories/:storyId",
    name: "story-detail",
    component: () => import("./views/StoryDetailView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/stories/:storyId/edit",
    name: "story-edit",
    component: () => import("./views/StoryEditView.vue"),
    meta: { showHeader: true, isForm: true },
  },
  {
    path: "/me/support/bug-report/confirmation",
    name: "bug-report-confirmation",
    component: () => import("./views/BugReportConfirmationView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/wishlist",
    name: "current-wishlist-detail",
    component: () => import("./views/WishListDetailView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/me/wishlist/edit",
    name: "current-wishlist-edit",
    component: () => import("./views/WishListEditView.vue"),
    meta: { showHeader: true, isForm: true },
  },
  {
    path: "/me/wishes/create",
    name: "current-wish-create",
    component: () => import("./views/WishListItemCreateView.vue"),
    meta: { showHeader: true, isForm: true },
  },
  {
    path: "/me/wishes/:itemId/edit",
    name: "current-wish-edit",
    component: () => import("./views/WishListItemEditView.vue"),
    meta: { showHeader: true, isForm: true },
  },
  {
    path: "/me/wishes/:itemId",
    name: "current-wish-detail",
    component: () => import("./views/WishListItemDetailView.vue"),
    meta: { showHeader: true },
  },
  {
    path: "/u/:username/wishlist",
    name: "current-wishlist-public",
    component: () => import("./views/PublicWishListView.vue"),
    meta: { showHeader: true, publicPage: true },
  },
  {
    path: "/u/:username/wishes/:itemId",
    name: "current-wishlist-public-item",
    component: () => import("./views/PublicWishListItemDetailView.vue"),
    meta: { showHeader: true, publicPage: true },
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  /**
   * Handle route scrolling globally.
   *
   * @param {Object} to Target route
   * @param {Object} from Source route
   * @param {Object|null} savedPosition Native saved scroll position
   * @returns {Object}
   */
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return {
        el: to.hash,
      };
    }

    return { top: 0 };
  },
});

router.beforeEach(async (to) => {
  const isAuthPage = to.meta && to.meta.authPage;
  const isOnboardingPage = to.meta && to.meta.onboardingStep;
  const isPublicPage = to.meta && to.meta.publicPage;
  let accessToken = getAccessToken();

  if (!accessToken) {
    try {
      accessToken = await restoreAccessSession();
    } catch (error) {
      if (isSessionInvalidError(error)) {
        clearAccessToken();
        if (isAuthPage || isPublicPage) {
          return true;
        }
        return { path: "/auth/login", query: { redirect: to.fullPath } };
      }
      if (isAuthPage || isPublicPage) {
        return true;
      }
      return { path: "/auth/login", query: { redirect: to.fullPath } };
    }
  }

  if (!accessToken) {
    if (isAuthPage || isPublicPage) {
      return true;
    }
    return { path: "/auth/login", query: { redirect: to.fullPath } };
  }

  let onboardingStatus;
  try {
    onboardingStatus = await fetchOnboardingStatus();
  } catch (error) {
    if (isSessionInvalidError(error)) {
      clearAccessToken();
      if (isAuthPage) {
        return true;
      }
      return { path: "/auth/login", query: { redirect: to.fullPath } };
    }
    if (isAuthPage || isPublicPage) {
      return true;
    }
    return false;
  }

  if (!onboardingStatus.email_verified) {
    if (to.path.startsWith("/auth/verify-email")) {
      return true;
    }
    return {
      path: "/auth/verify-email",
      query: {
        email: onboardingStatus.email || undefined,
      },
    };
  }

  if (!onboardingStatus.is_complete) {
    const nextStepPath = onboardingStepRoutes[onboardingStatus.next_step] || onboardingStepRoutes[1];
    if (to.path !== nextStepPath) {
      return { path: nextStepPath };
    }
    return true;
  }

  if (isOnboardingPage || isAuthPage) {
    return { path: defaultPostOnboardingPath };
  }

  return true;
});

router.afterEach((to) => {
  persistLastVisitedRoute(to);
});

export default router;
